import { Box, Skeleton } from "@mui/material";
import { type FC, useState } from "react";
import { CONTROL_HEIGHT } from "@/theme/tokens";
import { type RandomRange, randomBetween, randomCount } from "./skeletonVariants";

/** How the grid varies per mount: its column and row counts, and each cell's bar width (% of the cell). */
const GRID_RANGES = {
	columns: [4, 6],
	rows: [7, 10],
	cellWidth: [30, 70],
} as const satisfies Record<string, RandomRange>;

/** A toolbar over a header row and a few body rows: a data grid's shape, with columns and cell widths drawn at random. */
export const GridSkeletonVariant: FC = () => {
	const [layout] = useState(() => {
		const columns = randomCount(GRID_RANGES.columns);
		const rows = Array.from({ length: randomCount(GRID_RANGES.rows) }, () => Array.from({ length: columns }, () => randomBetween(GRID_RANGES.cellWidth)));
		return { columns, rows };
	});

	return (
		<>
			<Box sx={{ display: "flex", gap: 1, mb: 2 }}>
				<Skeleton variant="rounded" sx={{ flex: 1, maxWidth: "30%", height: CONTROL_HEIGHT.STANDARD, borderRadius: 1 }} />
				<Skeleton variant="rounded" sx={{ width: "10%", height: CONTROL_HEIGHT.STANDARD, borderRadius: 1 }} />
			</Box>
			<Box sx={{ display: "grid", gridTemplateColumns: `repeat(${layout.columns}, 1fr)`, gap: 2, pb: 1 }}>
				{Array.from({ length: layout.columns }, (_, column) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: placeholder columns are a fixed row of bars; nothing reorders them.
					<Skeleton key={column} variant="text" width="60%" />
				))}
			</Box>
			{layout.rows.map((cellWidths, row) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: placeholder rows are a fixed stack of bars; nothing reorders them.
				<Box key={row} sx={{ display: "grid", gridTemplateColumns: `repeat(${layout.columns}, 1fr)`, gap: 2, py: 1 }}>
					{cellWidths.map((width, column) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: placeholder cells are a fixed row of bars; nothing reorders them.
						<Skeleton key={column} variant="text" width={`${width}%`} />
					))}
				</Box>
			))}
		</>
	);
};
